import { useLazyUser } from "$global-providers/user-provider";
import { gql } from "@apollo/client";
import type {
  EpocusProbeAssociationSyncPushDocument,
  QueryPullEpocusProbeAssociationArgs,
} from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { EpocusProbeAssociation } from "../collections/epocus-probe-association";
import type { RxDBCollections } from "../provider";
import {
  SyncEpocusProbeAssociationPullDocument,
  SyncEpocusProbeAssociationPushDocument,
  type WholeSyncableEpocusProbeAssociationFragment,
} from "./__generated__/epocus-probe-association.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  EPOCUS_PROBE_ASSOCIATION_ROLES,
  isReplicationRoleAllowed,
} from "./replication-role-allowlist";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateEpocusProbeAssociation = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<EpocusProbeAssociation>;
}) => {
  const role = useLazyUser()?.role;
  const allowed = isReplicationRoleAllowed(
    EPOCUS_PROBE_ASSOCIATION_ROLES,
    role,
  );

  const push = useMemo(
    () => ({
      mutation: SyncEpocusProbeAssociationPushDocument,
      mapRxToGql: mapRxEpocusProbeAssociationToGqlInput,
    }),
    [],
  );

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableEpocusProbeAssociationFragment>
  >(
    () => ({
      query: SyncEpocusProbeAssociationPullDocument,
      reSyncInterval: { minutes: 5 },
      ...createPersistedCheckpointPullBuilders<
        QueryPullEpocusProbeAssociationArgs,
        WholeSyncableEpocusProbeAssociationFragment
      >({
        buildInputVariables: (checkpoint, batchSize) => ({
          input: {
            limit: batchSize,
            checkpoint,
          },
        }),
        buildPullCheckpoint: (doc) => ({
          id: doc.id,
          updatedAt: doc.meta.updatedAt,
        }),
        entityId: "global",
        db,
        collectionName: collection?.name,
      }),
    }),
    [db, collection],
  );

  return useGqlReplication<
    EpocusProbeAssociation,
    WholeSyncableEpocusProbeAssociationFragment,
    EpocusProbeAssociationSyncPushDocument
  >({
    db,
    collection,
    push: allowed ? push : undefined,
    pull: allowed ? pull : undefined,
    mapGqlToRx: mapGqlEpocusProbeAssociationToRx,
    batchSize: 100,
    expireData: false,
    metadataLocalStorageKeySuffix: "epocus-probe-association",
    enabled: allowed,
  });
};

export const mapGqlEpocusProbeAssociationToRx = (
  doc: WholeSyncableEpocusProbeAssociationFragment,
): WithDeleted<EpocusProbeAssociation> => ({
  id: doc.id,
  deviceSerialNumber: doc.deviceSerialNumber,
  performerId: doc.performerId,
  performerName: doc.performerName,
  programId: doc.programId ?? null,
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
  },
  _deleted: doc.meta.deleted,
});

export const mapRxEpocusProbeAssociationToGqlInput = (
  doc: WithDeleted<EpocusProbeAssociation>,
): EpocusProbeAssociationSyncPushDocument => ({
  id: doc.id,
  // The resolver stores this verbatim while matching compares trimmed values,
  // so an untrimmed serial would persist and then silently never match.
  deviceSerialNumber: doc.deviceSerialNumber.trim(),
  performerId: doc.performerId,
  performerName: doc.performerName,
  programId: doc.programId ?? null,
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
    deleted: doc._deleted,
  },
});

gql`
  fragment WholeSyncableEpocusProbeAssociation on SyncableEpocusProbeAssociation {
    id
    deviceSerialNumber
    performerId
    performerName
    programId
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncEpocusProbeAssociationPull(
    $input: PullEpocusProbeAssociationInput!
  ) {
    pullEpocusProbeAssociation(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableEpocusProbeAssociation
      }
    }
  }
`;

gql`
  mutation SyncEpocusProbeAssociationPush(
    $input: PushEpocusProbeAssociationInput!
  ) {
    pushEpocusProbeAssociation(input: $input) {
      rows {
        ...WholeSyncableEpocusProbeAssociation
      }
    }
  }
`;
